import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Table, Button, Spinner, Badge } from 'react-bootstrap';
import { FaXRay, FaCalculator, FaMoneyBillWave, FaChartLine, FaSearch, FaUndo, FaFlask } from 'react-icons/fa';
import { MdDateRange } from 'react-icons/md';
import axios from 'axios';
import moment from 'moment';
import PageLayout from "../../../layouts/PageLayout";
import { FaArrowRotateLeft } from 'react-icons/fa6';

// Configuration for API URL base via .env configuration environment variables
const API = process.env.REACT_APP_API_URL || '';

const RadiologyReport = () => {
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({ total_cost: 0, total_sale_price: 0, total_profit: 0 });
    const [loading, setLoading] = useState(false);

    // Fallback or hook implementation for AuthToken context
    const AuthToken = localStorage.getItem('authToken') || '';

    // Filters State
    const [filters, setFilters] = useState({
        month: '',
        radiology_id: '',
        search: ''
    });

    // Fetch report from Laravel backend
    const fetchReport = async () => {
        setLoading(true);
        try {
            // Build query params dynamically omitting empty values
            const params = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) => value !== '')
            );

            const response = await axios.get(`${API}/radiology-report`, {
                params,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            setReportData(response.data.data || []);
            setSummary(response.data.summary || { total_cost: 0, total_sale_price: 0, total_profit: 0 });
        } catch (error) {
            console.error("Error loading radiology report:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchReport();
    };

    const handleReset = async () => {
        const resetFilters = { month: '', radiology_id: '', search: '' };
        setFilters(resetFilters);
        setLoading(true);
        try {
            const response = await axios.get(`${API}/raadiology-report`, {
                headers: { Authorization: `Bearer ${AuthToken}` }
            });
            setReportData(response.data.data || []);
            setSummary(response.data.summary || { total_cost: 0, total_sale_price: 0, total_profit: 0 });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Formats cleanly without dollar signs
    const formatNumberValue = (value) => Number(value);

    const totalCost = reportData.reduce((acc, row) => acc + parseFloat(row.radiology?.cost_price || 0), 0);
    const totalRevenue = reportData.reduce((acc, row) => acc + parseFloat(row.price || 0), 0);
    const totalNetProfit = totalRevenue - totalCost;

    return (
        <PageLayout>
            <Container fluid className="p-4 bg-light min-vh-100">
                {/* Page Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                            <FaXRay className="text-primary" /> {('Radiology Report')}
                        </h4>
                        <p className="text-muted small mb-0">{('Analyze radiology test performance, quantities, and profitability records.')}</p>
                    </div>
                </div>

                {/* ---- KPI Dashboard Summary Cards ---- */}
                <Row className="g-3 mb-4">
                    <Col xl={4} md={4} sm={12}>
                        <Card className="border-0 shadow-sm rounded-3 bg-white h-100">
                            <Card.Body className="p-3 d-flex align-items-center">
                                <div className="bg-info-subtle text-info p-3 rounded-3 me-3 d-inline-flex">
                                    <FaCalculator size={22} />
                                </div>
                                <div>
                                    <span className="text-secondary small d-block fw-semibold uppercase tracking-wider">{('TOTAL COSTS')}</span>
                                    <h3 className="fw-bold text-dark mb-0 mt-0.5">{formatNumberValue(totalCost)}</h3>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={4} md={4} sm={12}>
                        <Card className="border-0 shadow-sm rounded-3 bg-white h-100">
                            <Card.Body className="p-3 d-flex align-items-center">
                                <div className="bg-success-subtle text-success p-3 rounded-3 me-3 d-inline-flex">
                                    <FaMoneyBillWave size={22} />
                                </div>
                                <div>
                                    <span className="text-secondary small d-block fw-semibold uppercase tracking-wider">{('TOTAL REVENUE')}</span>
                                    <h3 className="fw-bold text-dark mb-0 mt-0.5">{formatNumberValue(totalRevenue)}</h3>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xl={4} md={4} sm={12}>
                        <Card className="border-0 shadow-sm rounded-3 bg-white h-100">
                            <Card.Body className="p-3 d-flex align-items-center">
                                <div className="bg-primary-subtle text-primary p-3 rounded-3 me-3 d-inline-flex">
                                    <FaChartLine size={22} />
                                </div>
                                <div>
                                    <span className="text-secondary small d-block fw-semibold uppercase tracking-wider">{('TOTAL NET PROFIT')}</span>
                                    <h3 className={`fw-bold mb-0 mt-0.5 ${totalNetProfit >= 0 ? 'text-primary' : 'text-danger'}`}>
                                        {formatNumberValue(totalNetProfit)}
                                    </h3>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* ---- Advanced Filters Panel ---- */}
                <Card className="border-0 shadow-sm rounded-3 mb-4 bg-white">
                    <Card.Body className="p-3">
                        <Form onSubmit={handleSearch}>
                            <Row className="g-3 align-items-end">
                                <Col lg={3} md={6}>
                                    <Form.Label className="small fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: '0.7rem' }}>
                                        <MdDateRange className="me-1" /> {('Select Month')}
                                    </Form.Label>
                                    <Form.Control
                                        type="month"
                                        name="month"
                                        size="sm"
                                        className="bg-white border rounded-3 shadow-none text-dark px-2.5"
                                        style={{ height: "38px" }}
                                        value={filters.month || new Date().toISOString().slice(0, 7)}
                                        onChange={handleInputChange}
                                    />
                                </Col>

                                <Col lg={4} md={8}>
                                    <Form.Label className="small fw-bold text-secondary text-uppercase mb-1" style={{ fontSize: '0.7rem' }}>
                                        {('Search Radiology Name')}
                                    </Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white text-muted border border-end-0 rounded-start-3" style={{ height: "38px" }}>
                                            <FaSearch size={13} />
                                        </span>
                                        <Form.Control
                                            type="text"
                                            name="search"
                                            size="sm"
                                            placeholder={('e.g. X-Ray, MRI, CT Scan...')}
                                            className="bg-white border border-start-0 rounded-end-3 shadow-none text-dark px-2.5"
                                            style={{ height: "38px" }}
                                            value={filters.search}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </Col>
                                <Col lg={2} md={4} className="d-flex gap-2">
                                    <Button type="submit" variant="primary" className="fw-semibold rounded-3 flex-fill text-nowrap" style={{ height: "38px" }} disabled={loading}>
                                        {loading ? <Spinner animation="border" size="sm" /> : ('Filter')}
                                    </Button>
                                    <Button
                                        type="button"
                                        className="bg-dark text-white border rounded-3 d-inline-flex align-items-center justify-content-center px-3"
                                        style={{ height: "38px" }}
                                        onClick={handleReset}
                                    >
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>

                {/* ---- Data Results Table ---- */}
                <Card className="border-0 shadow-sm rounded-3 bg-white overflow-hidden">
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table hover align="middle" className="mb-0 custom-report-table">
                                <thead className="bg-primary text-white text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                    <tr>
                                        <th className="py-3 text-white">Date</th>
                                        <th className="py-3 text-white">Radiology Name</th>
                                        <th className="py-3 text-end text-white">Cost</th>
                                        <th className="py-3 text-end text-white">Sale Price</th>
                                        <th className="py-3 text-end text-white">Profit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5 text-secondary">
                                                <Spinner animation="border" variant="primary" size="md" className="mb-2" />
                                                <div className="small fw-medium">Loading report datasets...</div>
                                            </td>
                                        </tr>
                                    ) : reportData.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5 text-muted">
                                                <FaFlask size={30} className="mb-2 text-black-50" />
                                                <div className="small fw-semibold">No lab test datasets discovered matching selection filters.</div>
                                            </td>
                                        </tr>
                                    ) : (
                                        <>
                                            {reportData.map((row) => {
                                                // Extract and normalize numerical data safely
                                                const costPrice = parseFloat(row.radiology?.cost_price || 0);
                                                const salePrice = parseFloat(row.price || 0);
                                                const inlineProfit = salePrice - costPrice;

                                                return (
                                                    <tr key={row.id} style={{ fontSize: '0.9rem' }}>
                                                        <td className="fw-bold text-dark-emphasis">
                                                            {row.radiology_date ? new Intl.DateTimeFormat('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            }).format(new Date(row.radiology_date.replace(' ', 'T'))) : '—'}
                                                        </td>
                                                        <td className="fw-bold text-dark-emphasis">{row.radiology?.name}</td>
                                                        <td className="text-end text-dark-emphasis fw-medium">
                                                            {formatNumberValue(costPrice)}
                                                        </td>
                                                        <td className="text-end text-success-emphasis fw-semibold">
                                                            {formatNumberValue(salePrice)}
                                                        </td>
                                                        <td className={`text-end fw-semibold ${inlineProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                            {formatNumberValue(inlineProfit)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {/* ---- Metrics Summary Summary Row ---- */}
                                            {(() => {

                                                return (
                                                    <tr className="bg-light fw-bold table-active" style={{ borderTop: '2px solid #dee2e6' }}>
                                                        <td colSpan="2" className="py-3 text-dark text-uppercase" style={{ fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                                                            Total Summary
                                                        </td>
                                                        <td className="py-3 text-end text-dark">
                                                            {formatNumberValue(totalCost)}
                                                        </td>
                                                        <td className="py-3 text-end text-primary">
                                                            {formatNumberValue(totalRevenue)}
                                                        </td>
                                                        <td className={`py-3 text-end ${totalNetProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                            {formatNumberValue(totalNetProfit)}
                                                        </td>
                                                    </tr>
                                                );
                                            })()}
                                        </>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            </Container>
        </PageLayout>
    );
};

export default RadiologyReport;